// zero-dep 날짜 유틸(RFC-date-picker). native Date + Intl만 사용 — 런타임 date 라이브러리 미의존(RFC-design-system-split).
// 모든 비교는 "로컬 날짜(연·월·일)" 기준으로 정규화해 시분초/타임존 영향을 제거한다.

/** 로컬 자정으로 정규화한 새 Date. */
export const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** 해당 달의 1일(로컬 자정). */
export const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

/** delta개월 이동한 달의 1일. */
export const addMonths = (d: Date, delta: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + delta, 1);

/** delta일 이동한 날짜(로컬 자정). */
export const addDays = (d: Date, delta: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);

/** 두 날짜가 같은 날인가(연·월·일). null 안전. */
export const isSameDay = (a: Date | null | undefined, b: Date | null | undefined): boolean =>
  a != null &&
  b != null &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** 날짜만 비교. a<b → -1, a==b → 0, a>b → 1. */
export const compareDay = (a: Date, b: Date): number => {
  const av = startOfDay(a).getTime();
  const bv = startOfDay(b).getTime();
  return av < bv ? -1 : av > bv ? 1 : 0;
};

/** start < d < end (양 끝 제외 — 끝은 endpoint로 따로 렌더). */
export const isBetweenDay = (d: Date, start: Date, end: Date): boolean =>
  compareDay(d, start) > 0 && compareDay(d, end) < 0;

/**
 * 로캘 주 시작 요일(0=일 … 6=토). Intl `weekInfo`가 있으면 사용(1=월 … 7=일 → 0=일 기준으로 변환),
 * 없으면 일요일(0). 최신 런타임에만 weekInfo가 있어 방어적으로 처리한다.
 */
export const getLocaleWeekStart = (locale?: string): number => {
  try {
    const resolved = locale ?? (typeof navigator !== "undefined" ? navigator.language : undefined);
    const loc = new Intl.Locale(resolved ?? "en");
    const withWeekInfo = loc as Intl.Locale & {
      weekInfo?: { firstDay?: number };
      getWeekInfo?: () => { firstDay?: number };
    };
    const info = withWeekInfo.getWeekInfo?.() ?? withWeekInfo.weekInfo;
    if (info && typeof info.firstDay === "number") {
      return info.firstDay % 7; // Intl: 1=Mon..7=Sun → 7(Sun)%7=0
    }
  } catch {
    /* weekInfo 미지원 런타임 — 기본값 폴백 */
  }
  return 0;
};

/** "July 2026" 류 월·연 라벨. */
export const formatMonthYear = (d: Date, locale?: string): string =>
  new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(d);

/** 트리거/입력에 보이는 날짜 표기(로캘 medium). */
export const formatDate = (d: Date, locale?: string): string =>
  new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric" }).format(d);

/** 주 시작 요일부터 7개 요일 약자(예: Sun..Sat 또는 Mon..Sun). */
export const getWeekdayLabels = (weekStartsOn: number, locale?: string): string[] => {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  // 2021-08-01은 일요일 — 여기서 offset을 더해 라벨 순서를 만든다.
  const sunday = new Date(2021, 7, 1);
  return Array.from({ length: 7 }, (_, i) => fmt.format(addDays(sunday, (weekStartsOn + i) % 7)));
};
